import React, { useState, useEffect, startTransition } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Select,
    MenuItem,
    FormControl,
    CircularProgress,
    Box,
    Typography
} from '@mui/material';
import clientService from 'src/clientService';
import { useSnackbar } from 'src/view/components/SnackbarService';
import { useAppState } from 'src/state-management/state-provider';
import { Project, ProjectAdminSummary } from 'src/api/Api';

interface AssignProjectDialogProps {
    open: boolean;
    onClose: () => void;
    onProjectAssigned: () => void;
    userId: string;
    userEmail: string;
    assignedProjectIds: string[];
}

const AssignProjectDialog: React.FC<AssignProjectDialogProps> = ({ open, onClose, onProjectAssigned, userId, userEmail, assignedProjectIds }) => {
    const { state, dispatch } = useAppState();
    const [externalProjects, setExternalProjects] = useState<Project[]>([]);
    const [selectedProject, setSelectedProject] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [isAssigning, setIsAssigning] = useState<boolean>(false);
    const { showSnackbar, snackbarType } = useSnackbar();

    const isDarukaaUser = userEmail.endsWith('@darukaa.com');
    const projectsToShow = (isDarukaaUser ? state.admin.projects : externalProjects) as (Project | ProjectAdminSummary)[];

    useEffect(() => {
        if (!open) return;

        const fetchProjects = async () => {
            setLoading(true);
            setError(null);
            try {
                if (isDarukaaUser) {
                    if (state.admin.projects.length === 0) {
                        const response = await clientService.api.getAllProjectsAdminApiV1AdminProjectsGet();
                        startTransition(() => {
                            if (response && response.data) {
                                dispatch({ type: 'ADMIN_SET_PROJECTS', payload: response.data });
                            }
                        });
                    }
                } else {
                    const response = await clientService.api.getProjectsByOrgEmailApiV1AssignmentsProjectsByOrgEmailOrgEmailGet(userEmail);
                    startTransition(() => {
                        if (response && response.data) {
                            setExternalProjects(response.data);
                        }
                    });
                }
            } catch (err) {
                console.error('Failed to fetch projects:', err);
                setError('Failed to fetch projects.');
            } finally {
                setLoading(false);
            }
        };

        if (isDarukaaUser) {
            if (state.admin.projects.length === 0) fetchProjects();
        } else {
            fetchProjects();
        }
    }, [open, userEmail, isDarukaaUser, state.admin.projects.length, dispatch]);

    const handleAssign = async () => {
        if (!selectedProject) {
            showSnackbar({ message: 'Please select a project.', type: snackbarType.WARNING });
            return;
        }
        setIsAssigning(true);
        try {
            await clientService.api.createAssignmentEndpointApiV1AssignmentsPost({ project_id: selectedProject, user_id: userId });
            showSnackbar({ message: 'Project assigned successfully.', type: snackbarType.SUCCESS });
            onProjectAssigned();
            onClose();
        } catch (error: any) {
            const errorMessage = error?.detail?.[0]?.msg || error?.message || 'Failed to assign project.';
            showSnackbar({ message: `Error: ${errorMessage}`, type: snackbarType.ERROR });
        } finally {
            setIsAssigning(false);
        }
    };

    const unassignedProjects = projectsToShow.filter((p: Project | ProjectAdminSummary) => !assignedProjectIds.includes(p.project_id));

    if (loading) {
        return (
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle>Assign Project</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}><CircularProgress /></Box>
                </DialogContent>
            </Dialog>
        );
    }

    if (error) {
        return (
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle>Assign Project</DialogTitle>
                <DialogContent>
                    <Typography color="error">{error}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            fullWidth 
            maxWidth="sm"
            PaperProps={{
                sx: {
                    bgcolor: '#0D0D0D',
                    backgroundImage: 'none',
                    borderRadius: '20px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    p: 2
                }
            }}
        >
            <DialogTitle sx={{ color: '#fff', fontWeight: 800, fontSize: '1.5rem', pb: 1 }}>
                Assign Project
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', mb: 3, fontSize: '0.9rem' }}>
                    Select a project to assign to this user. Only unassigned projects are listed.
                </Typography>
                <FormControl fullWidth>
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', mb: 1, ml: 0.5 }}>
                        Project
                    </Typography>
                    <Select
                        value={selectedProject}
                        onChange={(e) => setSelectedProject(e.target.value as string)}
                        displayEmpty
                        renderValue={(value) => {
                            if (!value) return <Typography sx={{ color: 'rgba(255, 255, 255, 0.3)' }}>Choose a project...</Typography>;
                            const proj = projectsToShow.find(p => p.project_id === value);
                            return <Typography sx={{ color: '#fff' }}>{proj?.project_name}</Typography>;
                        }}
                        sx={{ 
                            bgcolor: 'rgba(255, 255, 255, 0.03)', 
                            color: '#fff',
                            borderRadius: '12px',
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                            '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.5)' }
                        }}
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    bgcolor: '#1A1A1A',
                                    color: '#fff',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    mt: 1,
                                    '& .MuiMenuItem-root': {
                                        fontSize: '0.9rem',
                                        py: 1.5,
                                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' }
                                    }
                                }
                            }
                        }}
                    >
                        {unassignedProjects.length > 0 ? (
                            unassignedProjects.map((project) => (
                                <MenuItem key={project.project_id} value={project.project_id}>
                                    {project.project_name}
                                </MenuItem>
                            ))
                        ) : (
                            <MenuItem disabled sx={{ color: 'rgba(255, 255, 255, 0.3)' }}>No unassigned projects available</MenuItem>
                        )}
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions sx={{ p: 3, gap: 1 }}>
                <Button 
                    onClick={onClose} 
                    disabled={isAssigning}
                    sx={{ color: 'rgba(255, 255, 255, 0.5)', textTransform: 'none', fontWeight: 600 }}
                >
                    Cancel
                </Button>
                <Button 
                    onClick={handleAssign} 
                    variant="contained" 
                    disabled={!selectedProject || isAssigning}
                    sx={{
                        bgcolor: '#10B981',
                        '&:hover': { bgcolor: '#059669' },
                        color: '#000',
                        fontWeight: 800,
                        textTransform: 'none',
                        px: 4,
                        borderRadius: '10px',
                        minWidth: '100px'
                    }}
                >
                    {isAssigning ? <CircularProgress size={24} sx={{ color: '#000' }} /> : 'Assign'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AssignProjectDialog;
